import React from 'react';

export default function Terms() {
  return (
    <main className="legal">
      <div className="legal-inner">
        <div className="legal-eyebrow">Legal</div>
        <h1>Terms &amp; Conditions</h1>
        <div className="legal-meta">Last updated: April 28, 2026</div>

        <p className="legal-intro">
          Please read these Terms &amp; Conditions (“Terms”) carefully before using PicAccess (“the App”).
          By downloading, installing, or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.
        </p>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">1.</span>Acceptance of Terms</h2>
          <p>
            By downloading, installing, or using PicAccess, you confirm that you have read, understood, and agree to be bound by these Terms &amp; Conditions.
            These Terms constitute a legally binding agreement between you and the developer of PicAccess (“we”, “us”, or “our”).
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">2.</span>Description of Service</h2>
          <p>
            PicAccess is a mobile application designed to provide quick, convenient access to important photos stored on your device — such as identification cards, insurance cards, or other documents you frequently need.
            The App allows you to store up to 5 photos locally on your device and optionally sync them via your personal iCloud account.
          </p>
          <p>
            The first photo slot is provided free of charge. Additional slots (slots 2–5) require a one-time in-app purchase (“Unlock All Slots”) to access.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">3.</span>In-App Purchases</h2>
          <p>
            PicAccess offers a one-time, non-consumable in-app purchase to unlock all 5 photo slots. All purchases are processed by Apple through the App Store and are subject to Apple’s payment terms.
            Purchases are non-refundable except as required by applicable law or Apple’s refund policies.
            You can restore a previous purchase at any time using the “Restore Purchases” option in Settings.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">4.</span>User Content &amp; Responsibility</h2>
          <p>You are solely responsible for the photos you store in the App. By using the App, you agree that:</p>
          <ul>
            <li>You will only store photos and content that you have the legal right to store.</li>
            <li>You will not use the App to store illegal, harmful, infringing, or otherwise prohibited content.</li>
            <li>The developer has no access to, control over, or responsibility for any content you store in the App.</li>
          </ul>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">5.</span>Privacy &amp; Data Collection</h2>
          <p>
            PicAccess does not collect, transmit, sell, or share any personal data, analytics, or usage information.
            Your photos remain entirely on your device and, if enabled, in your personal iCloud account. We have no ability to access your content.
            See our full <a href="/privacy">Privacy Policy</a> for details.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">6.</span>iCloud Sync</h2>
          <p>
            If iCloud is enabled on your device, photos saved in PicAccess may be synced across your devices via your personal iCloud account.
            This sync is governed by Apple’s iCloud Terms of Service. PicAccess has no access to or control over your iCloud data beyond what the operating system provides.
            Enabling or disabling iCloud sync is your responsibility and is managed through your device’s system settings.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">7.</span>Photo Library Access</h2>
          <p>
            The App requests access to your device’s photo library solely to allow you to select and save images within the App.
            We do not access, copy, or transmit any photos beyond what you explicitly choose to save.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">8.</span>Sensitive Information Warning</h2>
          <p>
            PicAccess is designed to provide convenient photo access and is commonly used to store sensitive documents such as ID cards, insurance cards, and similar items.
            You acknowledge that:
          </p>
          <ul>
            <li>Storing sensitive documents on your device carries inherent risk.</li>
            <li>The developer is not responsible for unauthorized access to your device or the photos stored within the App.</li>
            <li>You are strongly encouraged to use your device’s built-in security features — such as a device passcode, Face ID, or Touch ID — to protect your data.</li>
            <li>The optional Face ID / Touch ID lock feature within the App provides an additional layer of protection but does not guarantee absolute security.</li>
          </ul>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">9.</span>Intellectual Property</h2>
          <p>
            The App and its original content, design, features, and functionality are and will remain the exclusive property of the developer.
            All rights are reserved. You may not copy, modify, distribute, or create derivative works from the App or its content without express written permission.
          </p>
          <p>You retain full ownership of any photos or content you store within the App.</p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">10.</span>Disclaimer of Warranties</h2>
          <p>
            The App is provided on an “as is” and “as available” basis without warranties of any kind, either express or implied,
            including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that:
          </p>
          <ul>
            <li>The App will be uninterrupted, error-free, or free of harmful components.</li>
            <li>Defects will be corrected.</li>
            <li>The App or the servers that make it available are free of viruses or other harmful components.</li>
          </ul>
          <p>
            We are not responsible for any loss of data, including photos stored in the App, arising from device failure, software bugs, iCloud errors, or any other cause.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">11.</span>Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, in no event shall the developer be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages
            arising out of or in connection with your use of the App, including but not limited to:
          </p>
          <ul>
            <li>Loss of data or photos</li>
            <li>Loss of revenue or profits</li>
            <li>Damage to your device</li>
            <li>Unauthorized access to your stored content</li>
          </ul>
          <p>This limitation applies regardless of whether the developer has been advised of the possibility of such damages.</p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">12.</span>Termination</h2>
          <p>
            We reserve the right to terminate or suspend your access to the App at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, third parties, or for any other reason.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">13.</span>Changes to These Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Changes will be posted within the App or through an App Store update.
            Your continued use of the App after any changes constitutes your acceptance of the new Terms.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">14.</span>Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which the developer resides, without regard to conflict of law provisions.
          </p>
        </section>

        <hr className="legal-divider" />

        <section>
          <h2><span className="num">15.</span>Contact</h2>
          <p>If you have any questions about these Terms &amp; Conditions, please contact the developer through the App Store listing.</p>
        </section>
      </div>
    </main>
  );
}
